import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Category,
} from '@mui/icons-material';
import { getCategoryTypes, createCategory, updateCategory } from '../services/categories';

const CategoryFormDialog = ({ open, onClose, onSubmit, category, isEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    is_test: false,
    is_featured: false,
    show_on_home: false,
    display_order: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || '',
        is_test: category.is_test || false,
        is_featured: category.is_featured || false,
        show_on_home: category.show_on_home || false,
        display_order: category.display_order ?? 0,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: '',
        is_test: false,
        is_featured: false,
        show_on_home: false,
        display_order: 0,
      });
    }
    setErrors({});
  }, [category, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#374151',
        }}
      >
        <Category sx={{ color: '#4f46e5' }} />
        {isEdit ? 'Edit Category' : 'Add New Category'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4f46e5',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Icon (e.g. food-fork-drink, spa, car)"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                helperText="MaterialCommunityIcons name for mobile app"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4f46e5',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Home Display Order"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                helperText="Sorting position on Home screen (1, 2, 3...)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4f46e5',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4f46e5',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.show_on_home}
                      onChange={handleChange}
                      name="show_on_home"
                      sx={{
                        color: '#4f46e5',
                        '&.Mui-checked': {
                          color: '#4f46e5',
                        },
                      }}
                    />
                  }
                  label="Show on Mobile Home Page"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_featured}
                      onChange={handleChange}
                      name="is_featured"
                      sx={{
                        color: '#0284c7',
                        '&.Mui-checked': {
                          color: '#0284c7',
                        },
                      }}
                    />
                  }
                  label="Featured"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_test}
                      onChange={handleChange}
                      name="is_test"
                      sx={{
                        color: '#f59e0b',
                        '&.Mui-checked': {
                          color: '#f59e0b',
                        },
                      }}
                    />
                  }
                  label="Test Category"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              boxShadow: '0 6px 20px rgba(79, 70, 229, 0.4)',
            },
          }}
        >
          {isEdit ? 'Update Category' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getCategoryTypes();
      // Handle various response formats
      let categoriesData = [];
      if (Array.isArray(response)) {
        categoriesData = response;
      } else if (response && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (response && response.data && Array.isArray(response.data.categories)) {
        categoriesData = response.data.categories;
      } else if (response && typeof response === 'object') {
        const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
        categoriesData = possibleArrays.length > 0 ? possibleArrays[0] : [];
      }
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to fetch categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredCategories = categories.filter((category) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      category.name?.toLowerCase().includes(searchLower) ||
      category.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setIsEdit(false);
    setFormDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsEdit(true);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEdit) {
        await updateCategory(selectedCategory.id, formData);
        setSuccess('Category updated successfully!');
      } else {
        await createCategory(formData);
        setSuccess('Category created successfully!');
      }
      setFormDialogOpen(false);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(isEdit ? 'Failed to update category' : 'Failed to create category');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSuccess('Category deleted successfully!');
      setDeleteDialogOpen(false);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete category');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Category Types
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Manage shop categories and types
        </Typography>
      </Box>

      {/* Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search categories..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#4f46e5',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchCategories}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#4f46e5', borderColor: '#4f46e5' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCategory}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  boxShadow: '0 6px 20px rgba(79, 70, 229, 0.4)',
                },
              }}
            >
              Add Category
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      {/* Categories Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#4f46e5' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Icon</TableCell>
                    <TableCell>Home Page</TableCell>
                    <TableCell>Featured</TableCell>
                    <TableCell>Test</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Category sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                            No categories found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {searchTerm
                              ? 'Try adjusting your search criteria'
                              : 'Get started by adding a new category'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCategories
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((category, index) => (
                        <TableRow
                          key={category.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{category.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Category sx={{ fontSize: 20, color: 'white' }} />
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {category.name}
                                </Typography>
                                {category.description && (
                                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', maxWidth: 220 }} noWrap>
                                    {category.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={category.icon || '-'}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: '#4f46e5',
                                borderColor: 'rgba(79, 70, 229, 0.3)',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={category.show_on_home ? `Yes (#${category.display_order ?? 0})` : "No"}
                              size="small"
                              sx={{
                                background: category.show_on_home ? 'rgba(82, 173, 131, 0.15)' : 'rgba(0,0,0,0.05)',
                                color: category.show_on_home ? '#2e7d32' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={category.is_featured ? "Yes" : "No"}
                              size="small"
                              sx={{
                                background: category.is_featured ? 'rgba(2, 132, 199, 0.15)' : 'rgba(0,0,0,0.05)',
                                color: category.is_featured ? '#0284c7' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={category.is_test ? "Yes" : "No"}
                              size="small"
                              sx={{
                                background: category.is_test ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0,0,0,0.05)',
                                color: category.is_test ? '#f59e0b' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {category.createdAt
                                ? new Date(category.createdAt).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCategory(category)}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': { color: '#4f46e5' },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {/* Temporarily hidden
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(category)}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': { color: '#dc2626' },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              */}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredCategories.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Category Dialog */}
      <CategoryFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        category={selectedCategory}
        isEdit={isEdit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete Category
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6b7280' }}>
            Are you sure you want to delete <strong>{selectedCategory?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            Delete Category
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
